import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
    LayoutDashboard, Users, GraduationCap, FolderKanban,
    Sparkles, ListChecks, LogOut, Calendar as CalIcon, Menu, X, CreditCard,
    BookOpen, HelpCircle, Megaphone, Settings as SettingsIcon, Shield,
    ChevronsLeft, ChevronsRight,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useSidebarState } from "../hooks/useSidebarState";
import TrialBanner from "./TrialBanner";
import VerifyEmailBanner from "./VerifyEmailBanner";
import ReadOnlyBanner from "./ReadOnlyBanner";
import TrialEndedModal from "./TrialEndedModal";
import Footer from "./Footer";
import ReminderBell from "./ReminderBell";
import PWAInstallPrompt from "./PWAInstallPrompt";
import InstallAppMenuItem from "./InstallAppButton";

const LOGO = "https://customer-assets.emergentagent.com/job_project-sync-pro-1/artifacts/y56otaul_Virtually%20%281%29.png";

export default function Layout() {
    const { user, logout, effectiveRole, impersonation } = useAuth();
    const { t } = useTranslation();
    const nav = useNavigate();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const { collapsed, toggle } = useSidebarState();
    // Focus Mode strips all chrome (sidebar, banners, header padding) so the
    // current page can breathe. Toggled by any child page via a global custom event.
    const [focusMode, setFocusMode] = useState(false);

    useEffect(() => {
        const onFocus = (e) => setFocusMode(Boolean(e.detail));
        window.addEventListener("vu:focus-mode", onFocus);
        return () => window.removeEventListener("vu:focus-mode", onFocus);
    }, []);

    // Auto-exit focus mode on navigation so the user isn't stranded.
    useEffect(() => { setFocusMode(false); }, [location.pathname]);

    // Keyboard shortcuts:
    //   • "F" toggles Focus Mode
    //   • "Esc" exits Focus Mode
    // Suppressed while the user is typing (input / textarea / contenteditable) so
    // shortcuts never eat real keystrokes.
    useEffect(() => {
        const isTyping = (el) => {
            if (!el) return false;
            const tag = el.tagName;
            if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
            return el.isContentEditable === true;
        };
        const onKey = (e) => {
            if (e.metaKey || e.ctrlKey || e.altKey) return;
            if (isTyping(e.target)) return;
            if (e.key === "f" || e.key === "F") {
                e.preventDefault();
                setFocus(!focusMode);
            } else if (e.key === "Escape" && focusMode) {
                e.preventDefault();
                setFocus(false);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [focusMode]);

    // Local wrapper that also broadcasts the change so pages using
    // `setFocusMode(bool)` from Layout.jsx stay in sync.
    const setFocus = (on) => {
        setFocusMode(on);
        try { window.dispatchEvent(new CustomEvent("vu:focus-mode", { detail: !!on })); }
        catch { /* SSR */ }
    };

    const isStudent = (effectiveRole || user?.role) === "student";
    const items = [
        { to: "/dashboard", icon: LayoutDashboard, label: t("nav.dashboard") },
        isStudent
            ? { to: "/classes", icon: GraduationCap, label: t("nav.classes") }
            : { to: "/clients", icon: Users, label: t("nav.clients") },
        { to: "/projects", icon: FolderKanban, label: t("nav.projects") },
        ...(isStudent ? [{ to: "/study-hub", icon: BookOpen, label: t("nav.studyHub") }] : []),
        { to: "/personal", icon: Sparkles, label: t("nav.personal") },
        { to: "/todo", icon: ListChecks, label: t("nav.todo") },
        { to: "/calendar", icon: CalIcon, label: t("nav.calendar") },
        { to: "/social", icon: Megaphone, label: t("nav.social") },
        { to: "/billing", icon: CreditCard, label: t("nav.billing") },
        { to: "/settings", icon: SettingsIcon, label: t("nav.settings") },
        { to: "/help", icon: HelpCircle, label: t("nav.help") },
        // Founder-only entry — silently omitted for everyone else. The
        // /founder page also enforces the gate server- and client-side.
        ...(user?.is_founder ? [{ to: "/founder", icon: Shield, label: t("nav.founder") }] : []),
    ];

    const handleLogout = async () => {
        await logout();
        nav("/login");
    };

    const sidebarWidth = focusMode ? "w-0" : (collapsed ? "w-16" : "w-64");

    return (
        <div className="min-h-screen grain" data-testid="app-shell">
            <div className="flex">
                {/* Desktop sidebar — animated width. Only renders at ≥lg (1024 px)
                 * so tablets (768–1023 px) get the mobile drawer instead of a
                 * cramped in-page sidebar. */}
                <aside
                    data-testid="desktop-sidebar"
                    className={`hidden lg:flex ${sidebarWidth} shrink-0 flex-col gap-2 ${focusMode ? "p-0 overflow-hidden" : "p-3"} sticky top-0 h-screen border-r border-[rgba(26,19,24,0.08)] bg-[var(--bg-cream)] z-10 transition-[width,padding] duration-300 ease-in-out`}
                >
                    {!focusMode && (
                        <SidebarInner
                            mode={collapsed ? "desktop-collapsed" : "desktop-expanded"}
                            items={items}
                            user={user}
                            onToggle={toggle}
                            onLogout={handleLogout}
                            signOutLabel={t("nav.signOut")}
                        />
                    )}
                </aside>

                {/* Mobile drawer — overlay, doesn't compress content */}
                {mobileOpen && (
                    <div className="fixed inset-0 z-40 lg:hidden" data-testid="mobile-sidebar">
                        <div className="absolute inset-0 bg-black/40 animate-in fade-in duration-200"
                             onClick={() => setMobileOpen(false)} />
                        <aside className="absolute left-0 top-0 bottom-0 w-[85%] max-w-xs bg-[var(--bg-cream)] flex flex-col gap-2 p-4 shadow-2xl animate-in slide-in-from-left duration-200 overflow-y-auto" style={{ paddingTop: "max(1rem, env(safe-area-inset-top))", paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>
                            <button
                                onClick={() => setMobileOpen(false)}
                                aria-label="Close menu"
                                data-testid="mobile-sidebar-close"
                                className="self-end p-1 -mb-3 text-[var(--text-ink)]/60"
                            >
                                <X size={22} />
                            </button>
                            <SidebarInner
                                mode="mobile"
                                onNav={() => setMobileOpen(false)}
                                items={items}
                                user={user}
                                onToggle={toggle}
                                onLogout={handleLogout}
                                signOutLabel={t("nav.signOut")}
                            />
                        </aside>
                    </div>
                )}

                <main className="flex-1 min-w-0 relative z-[2]">
                    {/* Mobile top bar — tight spacing so bell + logout never collide with title.
                     * `paddingTop: env(safe-area-inset-top)` clears the iOS Dynamic Island / notch
                     * when the app is installed as a PWA. */}
                    {!focusMode && (
                        <div className="lg:hidden flex items-center gap-2 p-3 border-b border-[rgba(26,19,24,0.08)] bg-[var(--bg-cream)] sticky top-0 z-20" style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}>
                            <button
                                onClick={() => setMobileOpen(true)}
                                data-testid="mobile-menu-btn"
                                aria-label="Open menu"
                                className="w-11 h-11 grid place-items-center -ml-2 shrink-0 rounded-lg hover:bg-[rgba(26,19,24,0.05)] active:bg-[rgba(26,19,24,0.08)] transition"
                            >
                                <Menu size={22} />
                            </button>
                            <Link to="/dashboard" className="flex items-center gap-2 min-w-0 flex-1">
                                <img src={LOGO} alt="" className="h-8 w-8 object-contain shrink-0" />
                                <span className="font-serif font-semibold truncate text-sm">Virtually Unstoppable</span>
                            </Link>
                            <div className="flex items-center shrink-0">
                                <ReminderBell />
                                <button
                                    onClick={handleLogout}
                                    data-testid="mobile-logout-btn"
                                    aria-label="Sign out"
                                    className="w-11 h-11 grid place-items-center -mr-2 rounded-lg hover:bg-[rgba(26,19,24,0.05)] active:bg-[rgba(26,19,24,0.08)] transition"
                                >
                                    <LogOut size={18} />
                                </button>
                            </div>
                        </div>
                    )}
                    {/* Desktop: bell floats top-right so it stays visible on every page. */}
                    {!focusMode && (
                        <div className="hidden lg:block fixed top-4 right-6 z-[70]" data-testid="desktop-reminder-bell">
                            <ReminderBell />
                        </div>
                    )}
                    {/* Focus-mode exit pill — floats top-right while active. Brand-tinted,
                        subtle fade-in, keyboard hint baked in. */}
                    {focusMode && (
                        <button
                            onClick={() => setFocus(false)}
                            data-testid="focus-exit-pill"
                            aria-label="Exit focus mode"
                            className="fixed top-3 right-3 lg:top-4 lg:right-6 z-[80] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-[var(--text-ink)]/85 text-white shadow-lg backdrop-blur hover:bg-[var(--brand-purple)] transition-colors animate-in fade-in slide-in-from-top-2 duration-200"
                        >
                            <X size={13} />
                            <span>Exit Focus</span>
                            <kbd className="ml-1 px-1.5 py-0.5 rounded bg-white/15 text-[10px] tracking-wide">Esc</kbd>
                        </button>
                    )}
                    {/* Content area — expands into the space freed by a collapsed sidebar or focus mode.
                     * Padding + max-width step up at `lg` (1024 px) so tablets share the mobile
                     * spacing and never look like an under-sized desktop. */}
                    <div className={`${focusMode ? "p-3 lg:p-5" : "p-4 lg:p-8"} ${collapsed || focusMode ? "max-w-none" : "max-w-7xl"} mx-auto`}>
                        {!focusMode && <VerifyEmailBanner />}
                        {!focusMode && <ReadOnlyBanner />}
                        {!focusMode && <TrialBanner />}
                        <Outlet />
                    </div>
                    {!focusMode && <Footer />}
                </main>
            </div>
            <TrialEndedModal />
            {!focusMode && <PWAInstallPrompt />}
        </div>
    );
}

/**
 * Global helper so any page can enter/exit Focus Mode without prop drilling.
 *   setFocusMode(true)  → hides sidebar, banners, footer
 *   setFocusMode(false) → restores full chrome
 * Automatically resets on route change (see Layout's useEffect).
 */
export function setFocusMode(on) {
    try {
        window.dispatchEvent(new CustomEvent("vu:focus-mode", { detail: !!on }));
    } catch { /* SSR / older browsers */ }
}

/**
 * The sidebar contents. `mode` is one of:
 *   - "desktop-expanded"  → full labels + collapse button
 *   - "desktop-collapsed" → icon-only rail with hover tooltips
 *   - "mobile"            → full labels inside slide-out drawer
 * Defined at module scope so React sees a stable component type (no
 * subtree remount on every parent render).
 */
function SidebarInner({ mode, onNav, items, user, onToggle, onLogout, signOutLabel }) {
    const { t } = useTranslation();
    const isCollapsed = mode === "desktop-collapsed";
    return (
        <>
            <div className="flex items-center justify-between mb-6">
                <Link
                    to="/dashboard"
                    onClick={onNav}
                    className="flex items-center gap-2 min-w-0"
                    data-testid="sidebar-brand"
                    title={isCollapsed ? "Virtually Unstoppable" : undefined}
                >
                    <img src={LOGO} alt="Virtually Unstoppable" className="h-11 w-11 object-contain shrink-0" />
                    {!isCollapsed && (
                        <div className="leading-tight min-w-0">
                            <div className="font-serif text-lg font-semibold truncate">Virtually</div>
                            <div className="font-script text-[var(--brand-orange)] text-xl -mt-1 truncate">Unstoppable</div>
                        </div>
                    )}
                </Link>
                {mode !== "mobile" && (
                    <button
                        onClick={onToggle}
                        data-testid="sidebar-collapse-toggle"
                        aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                        title={isCollapsed ? "Expand" : "Collapse"}
                        className="p-1.5 rounded-lg text-[var(--text-ink)]/55 hover:text-[var(--brand-purple)] hover:bg-[rgba(123,45,142,0.08)] transition-colors shrink-0"
                    >
                        {isCollapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
                    </button>
                )}
            </div>

            <nav className="flex flex-col gap-1">
                {items.map((it) => (
                    <NavLink
                        key={it.to}
                        to={it.to}
                        onClick={onNav}
                        data-testid={`sidebar-nav-${it.to.replace(/^\//, "").replace(/\//g, "-")}`}
                        title={isCollapsed ? it.label : undefined}
                        className={({ isActive }) =>
                            `flex items-center ${isCollapsed ? "justify-center" : "gap-3"} px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                                isActive
                                    ? "bg-gradient-to-r from-[var(--brand-purple)]/12 to-[var(--brand-orange)]/12 text-[var(--brand-purple)]"
                                    : "text-[var(--text-ink)]/70 hover:bg-[rgba(255,107,53,0.08)]"
                            }`
                        }
                    >
                        <it.icon size={18} className="shrink-0" />
                        {!isCollapsed && <span className="truncate">{it.label}</span>}
                    </NavLink>
                ))}
            </nav>

            <div className="mt-auto pt-4 border-t border-[rgba(26,19,24,0.08)]">
                <div className={`flex items-center ${isCollapsed ? "justify-center" : "gap-3"} px-2 py-2`}>
                    {user?.picture ? (
                        <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" title={isCollapsed ? user?.name : undefined} />
                    ) : (
                        <div className="w-9 h-9 rounded-full brand-gradient text-white grid place-items-center font-semibold shrink-0"
                             title={isCollapsed ? user?.name : undefined}>
                            {(user?.name || "U")[0]?.toUpperCase()}
                        </div>
                    )}
                    {!isCollapsed && (
                        <div className="min-w-0">
                            <div className="text-sm font-medium truncate flex items-center gap-1.5">
                                <span className="truncate">{user?.name}</span>
                                {user?.is_founder && (
                                    <span
                                        data-testid="founder-badge"
                                        title={t("nav.founderBadge")}
                                        className="shrink-0 text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-gradient-to-r from-[var(--brand-purple)] to-[var(--brand-orange)] text-white"
                                    >{t("nav.founderBadge")}</span>
                                )}
                            </div>
                            <div className="text-xs text-[var(--text-ink)]/60">{user?.role === "student" ? t("nav.roleStudent") : t("nav.rolePro")}</div>
                        </div>
                    )}
                </div>
                <div className="mt-2">
                    <InstallAppMenuItem isCollapsed={isCollapsed} onNav={onNav} />
                </div>
                <button
                    onClick={onLogout}
                    data-testid="sidebar-logout-btn"
                    title={isCollapsed ? signOutLabel : undefined}
                    className={`mt-1 flex items-center ${isCollapsed ? "justify-center w-full" : "gap-2"} text-sm text-[var(--text-ink)]/70 hover:text-[var(--brand-magenta)] px-2 py-1.5`}
                >
                    <LogOut size={16} className="shrink-0" />
                    {!isCollapsed && <span>{signOutLabel}</span>}
                </button>
            </div>
        </>
    );
}